import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { Toggle } from "@/components/Toggle/Toggle";
import { useUpdateAiModelMutation } from "@/pages/Private/redux/media/media.api";
import { AiModel } from "@/pages/Private/redux/media/media.schema";

interface AiModelModalProps {
	model: AiModel | null;
	isOpened: boolean;
	handleClose: () => void;
}

export const AiModelModal: FunctionComponent<AiModelModalProps> = ({
	model,
	isOpened,
	handleClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`mediaPresets.${key}`);

	const [form, setForm] = useState({
		displayName: "",
		unitPriceUsd: 0,
		pricesVerified: false,
		enabled: true,
	});
	const [updateModel, { isLoading }] = useUpdateAiModelMutation();

	useEffect(() => {
		if (!model) {
			return;
		}

		setForm({
			displayName: model.displayName,
			unitPriceUsd: model.unitPriceUsd,
			pricesVerified: model.pricesVerified,
			enabled: model.enabled,
		});
	}, [model, isOpened]);

	const handleSave = async () => {
		if (!model) {
			return;
		}

		await updateModel({
			id: model.id,
			displayName: form.displayName,
			unitPriceUsd: form.unitPriceUsd,
			pricesVerified: form.pricesVerified,
			enabled: form.enabled,
		}).unwrap();

		handleClose();
	};

	return (
		<Modal
			handleClose={handleClose}
			handleSave={handleSave}
			isLoading={isLoading}
			isOpened={isOpened}
			size="sm"
			submitButtonText={t("basics.save")}
			title={ts("models.editTitle")}
		>
			<div className="space-y-4">
				<InputField
					handleChange={(event) =>
						setForm((prev) => ({ ...prev, displayName: event.target.value }))
					}
					label={ts("fields.displayName")}
					name="displayName"
					value={form.displayName}
				/>
				<InputField
					handleChange={(event) =>
						setForm((prev) => ({ ...prev, unitPriceUsd: +event.target.value }))
					}
					information={ts("fields.unitPriceHint")}
					label={ts("fields.unitPriceUsd")}
					name="unitPriceUsd"
					type="number"
					value={form.unitPriceUsd}
				/>

				<div className="flex items-start justify-between gap-4 rounded-card border border-border-soft p-3">
					<div>
						<p className="text-sm font-semibold text-ink">{ts("fields.pricesVerified")}</p>
						<p className="mt-1 text-xs text-subtle">{ts("fields.pricesVerifiedHint")}</p>
					</div>
					<Toggle
						isChecked={form.pricesVerified}
						onChange={() => setForm((prev) => ({ ...prev, pricesVerified: !prev.pricesVerified }))}
					/>
				</div>

				<div className="flex items-start justify-between gap-4 rounded-card border border-border-soft p-3">
					<p className="text-sm font-semibold text-ink">{ts("fields.enabled")}</p>
					<Toggle
						isChecked={form.enabled}
						onChange={() => setForm((prev) => ({ ...prev, enabled: !prev.enabled }))}
					/>
				</div>
			</div>
		</Modal>
	);
};
